import { motion } from "framer-motion";
import { FaWhatsapp, FaCrown, FaUsers, FaStar } from "react-icons/fa";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

const WhatsAppFloatButton = () => {
  const [isHovered, setIsHovered] = useState(false);
  const [isAmbassadorHovered, setIsAmbassadorHovered] = useState(false);
  const navigate = useNavigate();

  const handleAmbassadorClick = () => {
    // MASA Youth Ambassadors button click pe redirect
    navigate("/MASAYouthAmbassadors");
  };

  const handleWhatsAppClick = () => {
    // Original WhatsApp button functionality
    window.open("https://chat.whatsapp.com/FlclCNQJAjvLvROPHuhbJO", "_blank");
  };

  return (
    <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end gap-4">
      
      {/* MASA Youth Ambassadors Button */}
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0 }}
        animate={{ 
          opacity: 1, 
          y: 0, 
          scale: 1,
        }}
        transition={{
          duration: 1.2,
          delay: 0.3,
          ease: "easeOut",
        }}
        whileHover={{ 
          scale: 1.05,
          transition: { type: "spring", stiffness: 400, damping: 10 }
        }}
        whileTap={{ scale: 0.95 }}
        onMouseEnter={() => setIsAmbassadorHovered(true)}
        onMouseLeave={() => setIsAmbassadorHovered(false)}
      >
        {/* Glow Effect */}
        <motion.div
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.4, 0.6, 0.4]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute -inset-3 bg-gradient-to-r from-blue-500/20 via-purple-500/30 to-blue-500/20 rounded-full blur-md"
        />
        
        {/* Main Button */}
        <motion.button
          onClick={handleAmbassadorClick}
          animate={isAmbassadorHovered ? "hover" : "rest"}
          variants={{
            rest: { 
              backgroundColor: "#3B82F6",
              boxShadow: "0 8px 20px rgba(59, 130, 246, 0.4)"
            },
            hover: { 
              backgroundColor: "#2563EB",
              boxShadow: "0 12px 30px rgba(59, 130, 246, 0.6)"
            }
          }}
          className="relative flex items-center justify-center gap-2 text-white font-bold py-3 px-5 rounded-xl shadow-xl overflow-hidden group cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 50%, #60A5FA 100%)",
            border: "2px solid rgba(96, 165, 250, 0.7)"
          }}
        >
          {/* Shining Overlay */}
          <motion.div
            animate={{
              x: ["-100%", "200%"],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          />

          {/* Users Icon */}
          <motion.div
            animate={{
              y: [0, -3, 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="relative z-10"
          >
            <FaUsers className="text-lg" />
          </motion.div>

          {/* Text */}
          <motion.div
            animate={isAmbassadorHovered ? { x: 3 } : { x: 0 }}
            className="relative z-10"
          >
            <span className="text-xs font-bold tracking-wide drop-shadow-md whitespace-nowrap">
              MASA Youth Ambassadors
            </span>
          </motion.div>

          {/* Particle Effects on Hover */}
          {isAmbassadorHovered && (
            <>
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ 
                  scale: 1.5, 
                  opacity: 0,
                  y: -15,
                  x: -8
                }}
                transition={{ duration: 0.5 }}
                className="absolute w-1.5 h-1.5 bg-blue-300 rounded-full"
              />
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ 
                  scale: 1.5, 
                  opacity: 0,
                  y: -12,
                  x: 10
                }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="absolute w-1.5 h-1.5 bg-purple-300 rounded-full"
              />
            </>
          )}
        </motion.button>
      </motion.div>

      {/* Original WhatsApp Button */}
      <motion.div
        initial={{ opacity: 0, y: 100, scale: 0 }}
        animate={{ 
          opacity: 1, 
          y: 0, 
          scale: 1,
          rotate: [0, 5, -5, 5, 0]
        }}
        transition={{
          duration: 1.5,
          ease: "easeOut",
          opacity: { duration: 1 },
          y: { type: "spring", stiffness: 100, damping: 15 },
          rotate: { delay: 1, duration: 0.5 }
        }}
        whileHover={{ 
          scale: 1.1,
          rotate: 5,
          transition: { type: "spring", stiffness: 400, damping: 10 }
        }}
        whileTap={{ scale: 0.95 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Golden Glow Effect */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.5, 0.8, 0.5]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute -inset-4 bg-gradient-to-r from-yellow-400/30 via-amber-500/40 to-yellow-400/30 rounded-full blur-xl"
        />
        
        {/* Pulse Rings */}
        <motion.div
          animate={{
            scale: [1, 1.5, 1.8],
            opacity: [0.7, 0.4, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeOut"
          }}
          className="absolute -inset-3 border-2 border-yellow-400/50 rounded-full"
        />
        
        <motion.div
          animate={{
            scale: [1, 1.3, 1.6],
            opacity: [0.5, 0.3, 0]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeOut",
            delay: 0.5
          }}
          className="absolute -inset-4 border-2 border-amber-400/30 rounded-full"
        />

        {/* Main Button */}
        <motion.button
          onClick={handleWhatsAppClick}
          animate={isHovered ? "hover" : "rest"}
          variants={{
            rest: { 
              backgroundColor: "#F59E0B",
              boxShadow: "0 10px 25px rgba(245, 158, 11, 0.4)"
            },
            hover: { 
              backgroundColor: "#D97706",
              boxShadow: "0 15px 35px rgba(245, 158, 11, 0.6)"
            }
          }}
          className="relative flex items-center justify-center gap-3 text-white font-bold py-4 px-6 rounded-2xl shadow-2xl overflow-hidden group cursor-pointer"
          style={{
            background: "linear-gradient(135deg, #F59E0B 0%, #D97706 50%, #FBBF24 100%)",
            border: "3px solid rgba(251, 191, 36, 0.7)"
          }}
        >
          {/* Shining Overlay */}
          <motion.div
            animate={{
              x: ["-100%", "200%"],
            }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
          />

          {/* Floating Icons */}
          <motion.div
            animate={{
              y: [0, -5, 0],
              rotate: [0, 5, 0]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="relative z-10"
          >
            <FaWhatsapp className="text-2xl" />
          </motion.div>

          {/* Text Container */}
          <motion.div
            animate={isHovered ? { x: 5 } : { x: 0 }}
            className="relative z-10"
          >
            <span className="hidden md:inline-block text-sm font-extrabold tracking-wider drop-shadow-lg">
             Join WhatsApp group
            </span>
          </motion.div>

          {/* Crown Icon */}
          <motion.div
            animate={{
              y: [0, -3, 0],
              rotate: [0, 10, -10, 0]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute -top-2 -right-2"
          >
            <FaCrown className="text-yellow-300 text-lg drop-shadow-lg" />
          </motion.div>

          {/* Floating Stars */}
          <motion.div
            animate={{
              y: [0, -10, 0],
              rotate: 360
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute -top-3 left-2"
          >
            <FaStar className="text-yellow-200 text-xs" />
          </motion.div>
          
          <motion.div
            animate={{
              y: [0, -8, 0],
              rotate: -360
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "linear",
              delay: 0.5
            }}
            className="absolute -bottom-2 right-3"
          >
            <FaStar className="text-yellow-200 text-xs" />
          </motion.div>

          {/* Particle Effects */}
          {isHovered && (
            <>
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ 
                  scale: 2, 
                  opacity: 0,
                  y: -20,
                  x: -10
                }}
                transition={{ duration: 0.5 }}
                className="absolute w-2 h-2 bg-yellow-300 rounded-full"
              />
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ 
                  scale: 2, 
                  opacity: 0,
                  y: -15,
                  x: 15
                }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="absolute w-2 h-2 bg-amber-300 rounded-full"
              />
              <motion.div
                initial={{ scale: 0, opacity: 1 }}
                animate={{ 
                  scale: 1.5, 
                  opacity: 0,
                  y: -25,
                  x: 5
                }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="absolute w-2 h-2 bg-yellow-400 rounded-full"
              />
            </>
          )}
        </motion.button>
      </motion.div>
    </div>
  );
};

export default WhatsAppFloatButton;